import React from 'react';

import Select from '@uikit/Form/Select/Select';
import { SVGIcon } from '@uikit/old/Icon/Icon';

import {
  FilterJoins,
  FilterOperationsMapForBoolean,
  FilterOperationsMapForDate,
  FilterOperationsMapForEnum,
  FilterOperationsMapForEnumString,
  FilterOperationsMapForNumber,
  FilterOperationsMapForString,
  FilterOperationsMapForStringMandatory,
} from '@constants/filters/filterOperations';

import useNonInitialEffect from '@hooks/useNonInitialEffect';

import TypedInput from './TypedInput';

import selectStyles from './selectStyles';

import './FiltersTooltipContentRaw.scss';

export type FilterField = {
  label: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'enum' | 'enumString' | 'stringNotEmpty' | 'date';
  inputOptions?: { label: string; value: any }[];
};

type HeaderFiltersTooltipContentRawType = {
  isAdditional: boolean;
  rawId: number;
  filterRawData: any;
  onChangeFilter: (filterRawKey: number, filterInputKey: string, value: number | string) => void;
  onDeleteFilterInput: () => void;
  filterFields: FilterField[];
  skipDayCheck?: boolean;
  /** Row hook; derived `-join`, `-field`, `-operation`, `-value`, `-remove` (nothing without it). */
  testId?: string;
};

const mappersByFieldType: {
  [key: string]: {
    mapper: { label: string; value: number }[];
    defaultValue: { label: string; value: number };
  };
} = {
  string: {
    mapper: FilterOperationsMapForString,
    defaultValue: FilterOperationsMapForString[2],
  },
  stringNotEmpty: {
    mapper: FilterOperationsMapForStringMandatory,
    defaultValue: FilterOperationsMapForStringMandatory[2],
  },
  number: {
    mapper: FilterOperationsMapForNumber,
    defaultValue: FilterOperationsMapForNumber[0],
  },
  boolean: {
    mapper: FilterOperationsMapForBoolean,
    defaultValue: FilterOperationsMapForBoolean[0],
  },
  enum: {
    mapper: FilterOperationsMapForEnum,
    defaultValue: FilterOperationsMapForEnum[0],
  },
  enumString: {
    mapper: FilterOperationsMapForEnumString,
    defaultValue: FilterOperationsMapForEnumString[0],
  },
  date: {
    mapper: FilterOperationsMapForDate,
    defaultValue: FilterOperationsMapForDate[0],
  },
};

const FiltersTooltipContentRaw = ({
  isAdditional = false,
  rawId,
  filterRawData,
  onChangeFilter,
  onDeleteFilterInput = () => null,
  filterFields,
  skipDayCheck,
  testId,
}: HeaderFiltersTooltipContentRawType): JSX.Element => {
  const hook = (part: string): string | undefined => (testId ? `${testId}-${part}` : undefined);
  const handleChangeInputValue = (value: number | string) => onChangeFilter(rawId, 'value', value);
  const handleChangeFieldValue = (value) => onChangeFilter(rawId, 'field', value);
  const handleChangeOperationValue = (value) => onChangeFilter(rawId, 'operation', value);
  if (!filterRawData) {
    return null;
  }
  const { join, field, operation } = filterRawData;
  const selectedField = filterFields.find(({ value }) => value === field) || filterFields[0];

  if (!selectedField) {
    return null;
  }

  const currentJoins = {
    label: FilterJoins[join]?.label || FilterJoins[0].label,
    value: join,
  };

  const currentField = {
    label: selectedField.label,
    value: field,
  };

  const currentFieldType: string = selectedField.type;
  const { mapper: currentOperationTypeMapper, defaultValue } =
    mappersByFieldType[currentFieldType] || mappersByFieldType.string;
  const currentOperation = currentOperationTypeMapper.find(({ value }) => value === operation);

  useNonInitialEffect(() => {
    handleChangeOperationValue(defaultValue.value);
    handleChangeInputValue('');
  }, [field]);

  return (
    <div
      className={`filters-tooltip-raw ${isAdditional ? 'is-additional-row' : ''}`}
      data-testid={testId}
    >
      {isAdditional ? (
        <Select
          defaultValue={FilterJoins[0]}
          options={FilterJoins}
          className="filters-tooltip-raw__select--joins"
          additionalStyles={selectStyles}
          value={currentJoins}
          onChange={({ value }) => {
            onChangeFilter(rawId, 'join', value);
          }}
          testId={hook('join')}
        />
      ) : null}
      <Select
        defaultValue={filterFields[0]}
        className="filters-tooltip-raw__select--fields"
        additionalStyles={selectStyles}
        options={filterFields}
        value={currentField}
        onChange={({ value }) => handleChangeFieldValue(value)}
        testId={hook('field')}
      />
      <Select
        defaultValue={defaultValue}
        options={currentOperationTypeMapper}
        // isDisabled={currentOperationTypeMapper?.length === 1}
        className="filters-tooltip-raw__select--operations"
        additionalStyles={selectStyles}
        value={currentOperation || defaultValue}
        onChange={({ value }) => handleChangeOperationValue(value)}
        testId={hook('operation')}
      />
      <TypedInput
        type={currentFieldType}
        value={filterRawData.value}
        filterRawData={filterRawData}
        filterFields={selectedField}
        onChange={handleChangeInputValue}
        placeholder="Value"
        skipDayCheck={skipDayCheck}
        testId={hook('value')}
      />
      {isAdditional ? (
        <div
          className="filters-tooltip-raw__cross"
          onClick={onDeleteFilterInput}
          tabIndex={0}
          role="button"
          data-testid={hook('remove')}
        >
          <SVGIcon icon="crossBlack" color="#BDBDBD" />
        </div>
      ) : null}
    </div>
  );
};

export default FiltersTooltipContentRaw;
